import { useCallback } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { IconBellFill18 } from "nucleo-ui-fill-18"
import { IconBellOutline18 } from "nucleo-ui-outline-18"

import type { SubscribableEntityType } from "@productlane/lib/src/browser"
import { IconButton, Tooltip } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import { toast } from "@/components/toast"
import { generateId } from "@/lib/id"
import { mutateOk, useZero } from "@/lib/zero"
import { NOTIFICATIONS_COPY as copy } from "@/screens/app/notifications/copy"

interface Props {
	entityType: SubscribableEntityType
	entityId: string
	className?: string
}

/** The caller's own follow state for an entity, and a toggle that confirms with a toast. */
export function useSubscriptionToggle(
	entityType: SubscribableEntityType,
	entityId: string,
) {
	const zero = useZero()
	const [subscription] = useQuery(
		queries.notificationSubscription({ entityType, entityId }),
	)
	const isSubscribed = subscription?.isSubscribed ?? false
	const subscriptionId = subscription?.id

	const toggle = useCallback(async () => {
		try {
			await mutateOk(
				zero.mutate(
					mutators.notificationSubscriptions.set({
						id: subscriptionId ?? generateId(),
						entityType,
						entityId,
						isSubscribed: !isSubscribed,
					}),
				),
				"server",
			)
			toast.success({
				title: isSubscribed ? copy.toasts.unsubscribed : copy.toasts.subscribed,
				icon: isSubscribed ? IconBellOutline18 : IconBellFill18,
			})
		} catch {
			toast.error({ title: copy.toasts.failed })
		}
	}, [zero, subscriptionId, entityType, entityId, isSubscribed])

	return { isSubscribed, toggle }
}

/** Linear-style follow toggle for any subscribable entity. */
export function SubscribeButton({ entityType, entityId, className }: Props) {
	const { isSubscribed, toggle } = useSubscriptionToggle(entityType, entityId)
	const label = isSubscribed
		? copy.subscribe.unsubscribe
		: copy.subscribe.subscribe

	return (
		<Tooltip
			label={
				isSubscribed
					? copy.subscribe.subscribedTooltip(entityType)
					: copy.subscribe.unsubscribedTooltip(entityType)
			}
		>
			<IconButton
				variant="ghost"
				aria-label={label}
				aria-pressed={isSubscribed}
				Icon={isSubscribed ? IconBellFill18 : IconBellOutline18}
				iconClassName="transform-gpu"
				className={className}
				onClick={() => void toggle()}
			/>
		</Tooltip>
	)
}
